import type { OnChanges, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
} from '@angular/core';
import { FileRead } from '@freelancer/file-read';
import { FileStorage, StorageBucket } from '@freelancer/file-storage';
import type { RichTextElement } from '@freelancer/html-renderer';
import { ModalService } from '@freelancer/ui';
import { BannerUpsellColor } from '@freelancer/ui/banner-upsell';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { IconColor } from '@freelancer/ui/icon';
import { ModalSize } from '@freelancer/ui/modal';
import {
  CustomBannerTextColorApi,
  CustomCtaActionApi,
} from 'api-typings/common/common';

@Component({
  selector: 'fl-custom-banner',
  template: `
    <div
      *ngIf="
        !isCreateOrEditMode && !backgroundImageLoaded && hasBackgroundImage
      "
      class="LoadingSpinnerContainer"
    >
      <fl-spinner flTrackingLabel="CustomBannerLoadingSpinner"></fl-spinner>
    </div>
    <fl-banner-upsell
      class="Banner"
      [class.Hidden]="
        !isCreateOrEditMode && !backgroundImageLoaded && hasBackgroundImage
      "
      flTrackingSection="CustomBanner"
      [closeable]="closeable"
      [closeButtonColor]="IconColor.FOREGROUND"
      [color]="
        textColor === CustomBannerTextColorApi.LIGHT
          ? BannerUpsellColor.LIGHT
          : BannerUpsellColor.DARK
      "
      [fullHeight]="true"
    >
      <fl-banner-upsell-background-image
        *ngIf="imageUrl"
        alt="Custom banner background image"
        i18n-alt="Custom banner background image alt text"
        [src]="imageUrl"
        (imageLoaded)="handleImageLoaded()"
      >
      </fl-banner-upsell-background-image>
      <fl-banner-upsell-title>
        {{ bannerTitle }}
      </fl-banner-upsell-title>

      <fl-banner-upsell-description>
        <div class="Container">
          {{ bannerBody }}
        </div>
      </fl-banner-upsell-description>

      <fl-banner-upsell-button>
        <fl-button
          *ngIf="ctaAction === CustomCtaActionApi.LINK"
          flTrackingLabel="CustomBannerButton"
          [link]="ctaLink"
          [newTab]="true"
          [color]="ButtonColor.SECONDARY"
          [size]="ButtonSize.SMALL"
        >
          {{ ctaText }}
        </fl-button>
        <fl-button
          *ngIf="ctaAction === CustomCtaActionApi.MODAL"
          flTrackingLabel="CustomBannerButton"
          [color]="ButtonColor.SECONDARY"
          [size]="ButtonSize.SMALL"
          (click)="openPopUpModal()"
        >
          {{ ctaText }}
        </fl-button>
      </fl-banner-upsell-button>
    </fl-banner-upsell>
  `,
  styleUrls: ['./custom-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomBannerComponent implements OnChanges {
  BannerUpsellColor = BannerUpsellColor;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  CustomBannerTextColorApi = CustomBannerTextColorApi;
  CustomCtaActionApi = CustomCtaActionApi;
  IconColor = IconColor;

  @Input() bannerTitle: string;
  @Input() bannerBody: string;
  @Input() textColor: CustomBannerTextColorApi;
  // for file uploaded in the frontend
  @Input() imageFile?: File;
  // for file that has been persisted in the backend
  @Input() imageFileUuid?: string;
  @Input() ctaText?: string;
  @Input() ctaAction?: CustomCtaActionApi;
  @Input() ctaLink?: string;
  @Input() modalTitle?: string;
  @Input() parsedModalBodyElements?: readonly RichTextElement[];
  @Input() closeable = false;
  @Input() isCreateOrEditMode = false;

  backgroundImageLoaded = false;
  hasBackgroundImage = false;

  imageUrl: string | undefined;

  constructor(
    private cd: ChangeDetectorRef,
    private fileRead: FileRead,
    private fileStorage: FileStorage,
    private modalService: ModalService,
  ) {}

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if ('imageFile' in changes || 'imageFileUuid' in changes) {
      this.hasBackgroundImage =
        this.imageFile !== undefined || this.imageFileUuid !== undefined;
      if (this.imageFile) {
        this.imageUrl = await this.fileRead.getBase64StringFromFile(
          this.imageFile,
        );
        this.cd.markForCheck();
        return;
      }

      if (this.imageFileUuid) {
        this.imageUrl = await this.fileStorage
          .getDownloadURL(StorageBucket.CUSTOM_BANNER, this.imageFileUuid)
          .then(response =>
            response.status === 'success' ? response.downloadURL : undefined,
          );
        this.cd.markForCheck();
      }
    }
  }

  handleImageLoaded(): void {
    this.backgroundImageLoaded = true;
  }

  openPopUpModal(): void {
    this.modalService.open('CustomBannerPopUpModal', {
      closeable: true,
      inputs: {
        heading: this.modalTitle,
        parsedBodyElements: this.parsedModalBodyElements,
      },
      size: ModalSize.MID,
      mobileFullscreen: true,
    });
  }
}
